import { Box, BoxProps } from "@mui/material";
import { ForwardedRef, forwardRef, useEffect, useRef, useState } from "react";
import { useWindowResize } from "../../hooks/useWindowResize";
import { CleaningMapDisplaySetting } from "../../types/type";

interface MapViewerProps extends BoxProps {
  src: string;
  displaySetting: CleaningMapDisplaySetting;
  mode?: "VIEW" | "EDIT";
}

export const MapViewer = forwardRef(
  (props: MapViewerProps, ref: ForwardedRef<HTMLImageElement>) => {
    const {
      src,
      sx,
      displaySetting: {
        degree,
        scale,
        offset,
        baseWidth: basicWidth,
        baseHeight: basicHeight,
      },
      mode = "VIEW",
      ...otherProps
    } = props;
    const [ratio, setRatio] = useState<number>(1);
    const [aspectRatio, setAspectRatio] = useState<number>(1);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const { windowWidth } = useWindowResize();
    useEffect(() => {
      if (!containerRef.current) return;

      if (ref || imageRef.current) {
        const targetRef = ref
          ? (ref as React.RefObject<HTMLImageElement>)
          : imageRef;

        if (basicWidth) {
          setRatio(containerRef.current.clientWidth / basicWidth);
        }

        if (!targetRef.current) return;
        if (!basicWidth) {
          setRatio(
            containerRef.current.clientWidth / targetRef.current.clientWidth
          );
        }

        setAspectRatio(
          targetRef.current.naturalWidth / targetRef.current.naturalHeight
        );
      }
    }, [windowWidth, basicWidth, ref, imageRef]);

    return (
      <Box
        sx={{
          overflow: "hidden",
          display: "inline-flex",
          border: "1px solid #dcdcdc",
          maxWidth: 400,
          aspectRatio: "1",
        }}
        ref={containerRef}
      >
        <Box
          sx={{
            transformOrigin: "0 0",
            scale: `${ratio}`,
            aspectRatio: 1,
            width:
              mode === "EDIT"
                ? "100%"
                : basicWidth
                ? `${basicWidth}px`
                : "100%",
            height:
              mode === "EDIT"
                ? "100%"
                : basicHeight
                ? `${basicHeight}px`
                : "auto",
          }}
        >
          <Box
            component="img"
            sx={{
              // 設定画面で利用しているライブラリではtransformに指定しているため、ここでもtransformにて一括で指定する
              transformOrigin: "center",
              transform: `translate(${offset.x}px, ${offset.y}px) rotate(${degree}deg) scale(${scale}) `,
              aspectRatio: `${aspectRatio}`,
              objectFit: "contain",
              width: "100%",
              maxWidth: "400px",
              maxHeight: "400px",
              ...sx,
            }}
            src={`${process.env.REACT_APP_S3_DOMAIN}/${src}`}
            {...otherProps}
            ref={ref || imageRef}
          />
        </Box>
      </Box>
    );
  }
);
